// Hạt linh khí xoáy vào ngọn Thất Diệm Đăng (UI_SPEC §18.1, M1 §5): mật độ sáng và tốc độ theo tốc độ
// hấp thụ (`setIntensity`), đèn bừng một nhịp khi đột phá tiểu cảnh (`pulse`).
// ParticleContainer, cập nhật ở 60Hz bằng ticker; không vẽ canvas 2D rồi upload mỗi khung.
// Mọi hạt luôn sống (tái sinh tại chỗ), nên không đụng hai bẫy của ParticleContainer
// (visible=false bị bỏ qua; container vẽ min(children, maxSize) con đầu bất kể alpha).
import { Container, Graphics, ParticleContainer, Sprite, type Application, type Texture } from 'pixi.js';
import { createRng, type Rng } from '@sim/core/rng';
import { PIXI_COLORS } from '@ui/tokens';
import { FRAME_H, FRAME_W } from './stage';
import { glowMesh } from './glow';

const GLOW_R = 110;

export const QI_PARTICLES = 480;
const LAMP_X = FRAME_W / 2;
const LAMP_Y = FRAME_H * 0.58;

/** Hạt đi theo xoắn ốc: bán kính co dần, góc quay nhanh dần khi tới gần đèn. */
interface Mote {
  s: Sprite;
  r: number;
  a: number;
  speed: number;
  life: number;
  ttl: number;
  base: number;
}

const SWIRL = 38;
const SQUASH_X = 0.7;

function dotTexture(app: Application): Texture {
  const g = new Graphics();
  for (let r = 6; r >= 1; r--)
    g.beginFill(0xffffff, 0.12 + (6 - r) * 0.14)
      .drawCircle(8, 8, r)
      .endFill();
  const tex = app.renderer.generateTexture(g, { resolution: 2 });
  g.destroy();
  return tex;
}

function spawn(m: Mote, rng: Rng, initial: boolean): void {
  m.a = rng.next() * Math.PI * 2;
  const r0 = 180 + rng.next() * 360;
  m.speed = 14 + rng.next() * 26;
  m.ttl = (r0 - 6) / m.speed;
  m.life = initial ? rng.next() * m.ttl : 0;
  m.r = r0 - m.speed * m.life;
  m.base = 0.35 + rng.next() * 0.55;
  m.s.scale.set(0.35 + rng.next() * 0.55);
  m.s.tint = rng.next() < 0.25 ? PIXI_COLORS.gold : PIXI_COLORS.jade;
}

export interface QiField {
  readonly root: Container;
  readonly count: number;
  /** 0.3 (chưa dẫn được khí) … 1.6 (Tử Quan): độ sáng và tốc độ hạt. */
  setIntensity(k: number): void;
  /** Đèn bừng một nhịp (đột phá tiểu cảnh). */
  pulse(): void;
  /** Màn Tu Luyện (đèn là vật thể chính) hay màn khác (chỉ còn hạt mờ làm nền). */
  setFocus(on: boolean): void;
  destroy(): void;
}

export function mountQiField(app: Application, seed = 1): QiField {
  const rng = createRng(seed).fork('qiField');
  const root = new Container();

  const glow = glowMesh(PIXI_COLORS.gold, GLOW_R);
  glow.mesh.position.set(LAMP_X, LAMP_Y);
  root.addChild(glow.mesh);

  const lamp = new Graphics();
  lamp.beginFill(PIXI_COLORS.gold, 0.9).drawPolygon([-7, 0, 0, -26, 7, 0, 0, 8]).endFill();
  lamp.beginFill(PIXI_COLORS.paper, 0.85).drawPolygon([-3, -2, 0, -15, 3, -2, 0, 3]).endFill();
  lamp.position.set(LAMP_X, LAMP_Y);
  root.addChild(lamp);

  const tex = dotTexture(app);
  const pc = new ParticleContainer(QI_PARTICLES, { position: true, tint: true, vertices: true });
  const motes: Mote[] = [];
  for (let i = 0; i < QI_PARTICLES; i++) {
    const s = new Sprite(tex);
    s.anchor.set(0.5);
    const m: Mote = { s, r: 0, a: 0, speed: 1, life: 0, ttl: 1, base: 1 };
    spawn(m, rng, true);
    pc.addChild(s);
    motes.push(m);
  }
  root.addChild(pc);
  app.stage.addChild(root);

  let t = 0;
  let focus = 1;
  let intensity = 1;
  let flare = 0;
  const tick = (): void => {
    if (!root.visible) return;
    const dt0 = Math.min(app.ticker.deltaMS / 1000, 0.1);
    // Đột phá: đèn bừng rồi tắt dần trong ~0,9s, hạt lao nhanh hơn trong lúc đó.
    flare = Math.max(0, flare - dt0 * 1.1);
    const dt = dt0 * (0.55 + 0.45 * intensity) * (1 + flare * 1.5);
    t += dt0;
    glow.uniforms.uAlpha = 0.3 + 0.2 * intensity + Math.sin(t * 1.3) * 0.06 + flare * 0.4;
    const alphaK = Math.min(1, 0.35 + 0.65 * intensity) * (0.45 + 0.55 * focus);
    for (let i = 0; i < motes.length; i++) {
      const m = motes[i]!;
      m.life += dt;
      if (m.life >= m.ttl) spawn(m, rng, false);
      m.r -= m.speed * dt;
      m.a += (SWIRL / Math.max(m.r, 24)) * dt;
      const k = m.life / m.ttl;
      // Mờ vào rồi tan khi chạm đèn.
      m.s.alpha = alphaK * m.base * Math.min(1, k * 4) * Math.min(1, (1 - k) * 3);
      m.s.position.set(LAMP_X + Math.cos(m.a) * m.r * SQUASH_X, LAMP_Y + Math.sin(m.a) * m.r);
    }
  };
  app.ticker.add(tick);

  return {
    root,
    count: motes.length,
    setIntensity(k) {
      intensity = Math.max(0, Math.min(2, k));
    },
    pulse() {
      flare = 1;
    },
    setFocus(on) {
      focus = on ? 1 : 0;
      glow.mesh.visible = on;
      lamp.visible = on;
    },
    destroy() {
      app.ticker.remove(tick);
      root.destroy({ children: true, texture: true, baseTexture: true });
    },
  };
}
